import React from 'react';
import Head from 'next/head';

import { localeToLang } from 'providers/prismic/utils/localeToLang';
import { getLayout, IPrismicLayout } from 'providers/prismic/layout';
import { Layout } from 'components/elements/layout/Layout';
import { config } from 'utils/config';

type Props = {
  layout: IPrismicLayout;
  currentSection: string;
};

export default function ElementsPage({ layout, currentSection }: Props) {
  return (
    <>
      <Head>
        <title>Elements</title>
      </Head>

      <Layout
        current={currentSection}
        sections={[
          {
            link: 'hero',
            name: 'Hero',
            description: 'Intro banner',
            Component: require('components/elements/slices/Hero').Component,
          },
          {
            link: 'text',
            name: 'Text',
            description: 'Text slices with optional columns',
            Component: require('components/elements/slices/Text').Component,
          },
          {
            link: 'text-block',
            name: 'Text Block',
            description: 'Text block slice with variable layout',
            Component: require('components/elements/slices/TextBlock').Component,
          },
          {
            link: 'quote',
            name: 'Quote',
            description: 'Quote slice',
            Component: require('components/elements/slices/Quote').Component,
          },
          {
            link: 'image-block',
            name: 'Image Block',
            description: 'Text Block & Image',
            Component: require('components/elements/slices/ImageBlock').Component,
          },
          {
            link: 'gallery-block',
            name: 'Gallery Block',
            description: 'Text Block & Gallery',
            Component: require('components/elements/slices/GalleryBlock').Component,
          },
          {
            link: 'block-list',
            name: 'Staggered Blocks',
            description: 'Alternating Block List',
            Component: require('components/elements/slices/Content').Component,
          },
          {
            link: 'video',
            name: 'Video',
            description: 'Video section',
            Component: require('components/elements/slices/Video').Component,
          },
          {
            link: 'video-list',
            name: 'Video List',
            description: 'Video list',
            Component: require('components/elements/slices/VideoList').Component,
          },
          {
            link: 'gallery',
            name: 'Gallery Slideshow',
            description: 'Gallery carousel',
            Component: require('components/elements/slices/Gallery').Component,
          },
          {
            link: 'gallery-grid',
            name: 'Gallery Grid',
            description: 'Gallery grid layout',
            Component: require('components/elements/slices/GalleryGrid').Component,
          },
          {
            link: 'column-section',
            name: 'Column Section',
            description: 'A section with up to three columns',
            Component: require('components/elements/slices/ColumnSection').Component,
          },
          {
            link: 'item-list',
            name: 'Item List',
            description: 'List of text items',
            Component: require('components/elements/slices/ItemList').Component,
          },
          {
            link: 'timeline',
            name: 'Timeline',
            description: 'Chronological list of events',
            Component: require('components/elements/slices/Timeline').Component,
          },
          {
            link: 'people',
            name: 'People',
            description: 'List of people and positions',
            Component: require('components/elements/slices/People').Component,
          },
          {
            link: 'pipeline',
            name: 'Pipeline',
            description: 'Pipeline section',
            Component: require('components/elements/slices/Pipeline').Component,
          },
          // {
          //   link: 'articles',
          //   name: 'Articles list',
          //   description: 'Three featured articles',
          //   Component: require('components/elements/slices/Articles').Component,
          // },
          {
            link: 'articles',
            name: 'Featured articles',
            description: 'Three featured articles',
            Component: require('components/elements/slices/FeaturedArticles').Component,
          },
          {
            link: 'media',
            name: 'Media List',
            description: 'List of media elements for download',
            Component: require('components/elements/slices/MediaList').Component,
          },
          {
            link: 'cta',
            name: 'CTA',
            description: 'Call to action',
            Component: require('components/elements/slices/CTA').Component,
          },
          {
            link: 'newsletter',
            name: 'Newsletter Signup',
            description: 'Newsletter Call to Action',
            Component: require('components/elements/slices/Newsletter').Component,
          },
          {
            link: 'forms',
            name: 'Forms',
            description: 'Forms',
            Component: require('components/elements/slices/Forms').Component,
          },
          {
            link: 'documents',
            name: 'Documents',
            description: 'List of documents for download',
            Component: require('components/elements/slices/Documents').Component,
          },
          {
            link: 'contact',
            name: 'Contact',
            description: 'Primary contact',
            Component: require('components/elements/slices/Contact').Component,
          },
          {
            link: 'offices',
            name: 'Offices Section',
            description: 'Section with a list of offices',
            Component: require('components/elements/slices/Offices').Component,
          },
          {
            link: 'catalog',
            name: 'Catalog',
            description: 'Product catalog',
            Component: require('components/elements/slices/Catalog').Component,
          },
          {
            link: 'infographics',
            name: 'Infographics',
            description: 'Infographics',
            Component: require('components/elements/slices/Infographics').Component,
          },
          {
            link: 'data',
            name: 'Data',
            description: 'Data lists',
            Component: require('components/elements/slices/Data').Component,
          },
          {
            link: 'stats',
            name: 'Stats',
            description: 'Set of key figures',
            Component: require('components/elements/slices/Stats').Component,
          },
          {
            link: 'events',
            name: 'Events',
            description: 'Upcoming and past events',
            Component: require('components/elements/slices/Events').Component,
          },
          {
            type: 'element',
            link: 'buttons',
            name: 'Buttons',
            description: 'Example of different buttons',
            Component: require('components/elements/buttons/Buttons').Buttons,
          },
          {
            type: 'element',
            link: 'globe',
            name: 'Globe',
            description: 'Threejs globe',
            Component: require('components/globe').Globe,
          },
          {
            type: 'element',
            link: 'footer',
            name: 'Footer',
            description: 'Global footer',
            Component: require('components/footer/Footer').Footer,
            componentProps: layout.footer,
          },
        ]}
      />
    </>
  );
}

export async function getStaticProps({ params, preview = false, previewData, locale }) {
  const lang = localeToLang(locale);
  const layout = await getLayout({ previewData, lang });
  const currentSection = params.element ? params.element[params?.element?.length - 1] : '';

  return {
    props: {
      preview,
      layout,
      currentSection,
    },
    revalidate: config.revalidate,
  };
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  };
}
